<!-- eslint-disable vue/no-restricted-html-elements -->
<template>
  <div class="country-selector">
    <form
      method="post"
      action="/localization"
      class="country-selector__form"
      @submit.prevent="submitForm"
    >
      <input type="hidden" name="_method" value="put" />

      <input type="hidden" name="return_to" :value="currentURL" />

      <input type="hidden" name="language_code" :value="languageCode" />

      <span
        v-if="flags[currentCountry]"
        class="country-selector__icon"
        :title="$variable('store.countryName')"
      >
        <img
          class="icon icon__flag"
          :src="
            selectedCountry ? flags[selectedCountry] : flags[currentCountry]
          "
        />
      </span>

      <select
        id="country-selector__select"
        class="country-selector__select"
        :aria-label="$string('general.site_footer.select_country')"
        name="country_code"
        @change="handleCountrySelect"
      >
        <option
          v-for="(country, index) of sortedCountryList"
          :key="index"
          :selected="checkSelectedCondition(country.handle)"
          :value="country.handle"
          v-html="country.name"
        />
      </select>
    </form>
  </div>
</template>

<script>
/**
 * Vue: Country selector (country-selector)
 * -----------------------------------------------------------------------------
 * Basic country selector form, submitted on select change.
 *
 * @param {String} currentCountry - Localization country ISO code.
 * @param {Array} countries - Array of localization countries.
 * @param {Object} flags - Object of country flags.
 * @param {String} currentLanguage - Localization language code.
 * @param {Array} themeCustomCountryCodes - Array of custom US country codes.
 *
 */

export default {
  name: "CountrySelector",

  props: {
    currentCountry: {
      type: String,
      default: "",
    },

    currentLanguage: {
      type: String,
      default: "",
    },

    countries: {
      type: Array,
      default: () => [],
    },

    themeCustomCountryCodes: {
      type: Array,
      default: () => [],
    },

    flags: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      selectedCountry: null,
      languageCode: "en",
      currentURL: "",
    };
  },

  computed: {
    /**
     * Sorts country list in alphabetical order
     * @param {Object}
     */
    sortedCountryList() {
      let countryList = [...this.countries];
      const customCountries = this.themeCustomCountryCodes.map((country) => ({
        ...country,
        handle: country.url,
      }));

      const otherCountries = countryList;

      customCountries.sort((a, b) => a.name.localeCompare(b.name));
      otherCountries.sort((a, b) => a.name.localeCompare(b.name));

      let sortedList = customCountries.length
        ? [
            ...customCountries,
            ...(otherCountries.length ? [{ url: null, name: "──────────" }] : []),
            ...otherCountries,
          ]
        : otherCountries;

        if (
          this.currentCountry == 'US'
          || (this.currentCountry == 'GB' && cnvs.store.shopDomain === '//dryrobe.com/')
          ) {
          // remove items with a handle of DE and DE2
          sortedList = sortedList.filter(
            (item) => item.handle !== "DE2" && item.handle !== "DE"
          )
        }

      return sortedList;
    },
  },

  mounted() {
    this.currentURL = `${window.location.pathname}?${new URLSearchParams(
      window.location.search
    )}${window.location.hash}`;
  },

  methods: {
    /**
     * Check if conditions are met before preselecting appropriate option
     * @param {String} handle - Country handle.
     */
    checkSelectedCondition(handle) {
      if (this.currentCountry === "DE" && this.currentLanguage === "en") {
        return handle === "DE2";
      }

      return handle === this.currentCountry;
    },

    /**
     * Handle country select event
     * @param {Object} event - Select event.
     */
    handleCountrySelect(event) {
      const countryCode = event.target.value;

      if (countryCode.startsWith("http")) {
          console.log("Redirecting to", countryCode);
          // setTimeout(() => {
            window.location.href = countryCode;
          // }, 250);
      } else {
        switch (countryCode) {
          case "DE":
            this.languageCode = "de";
            break;

          case "FR":
            this.languageCode = "fr";
            break;

          default:
            this.languageCode = "en";
            break;
        }

        const allCountries = [
          ...this.countries,
          ...this.themeCustomCountryCodes,
        ];
        const selectedCountry = allCountries.find(
          (country) => country.handle === countryCode
        );

        if (selectedCountry) {
          this.selectedCountry = selectedCountry.handle.replace(/[0-9]/g, "");

          switch (selectedCountry.handle) {
            case "US":
              window.location.href = "https://us.dryrobe.com/?country=US";
              return;

            case "DE2":
              localStorage.setItem(
                "redirect-modal-dismissed",
                JSON.stringify(selectedCountry)
              );
              window.location.href = `${cnvs.store.shopUrl}en-de`;
              return;
          }

          setTimeout(() => {
            localStorage.setItem(
              "redirect-modal-dismissed",
              JSON.stringify(selectedCountry)
            );
            this.submitForm(event);
          }, 50);
        }
      }
    },

    /**
     * Submit localization form.
     * @param {Object} event - Submit event.
     */
    submitForm(event) {
      const form = event.target.closest("form");
      form.submit();
    },
  },
};
</script>

<style lang="scss">
@import "./country-selector";
</style>
